/**
 * Hide the signed-in user's own Listening profile from discovery
 * (Search, Featured, Available now). Dashboard / Profile / Edit stay intact.
 */

import { listingIdOf } from './featured';

/**
 * Author user id on a denormalised listing or JSON:API resource.
 *
 * @param {Object} [listing]
 * @returns {string|null}
 */
export const listingAuthorId = listing =>
  listing?.author?.id?.uuid ||
  listing?.author?.id ||
  listing?.relationships?.author?.data?.id?.uuid ||
  listing?.relationships?.author?.data?.id ||
  null;

/**
 * Current user id as a UUID string.
 *
 * @param {Object} [currentUser]
 * @returns {string|null}
 */
export const currentUserIdOf = currentUser =>
  currentUser?.id?.uuid || (typeof currentUser?.id === 'string' ? currentUser.id : null);

/**
 * True when this listing is the signed-in user's own Listening profile.
 *
 * @param {Object} [listing]
 * @param {Object} [options]
 * @param {Object} [options.currentUser]
 * @param {Array<string>} [options.ownListingIds]
 * @returns {boolean}
 */
export const isOwnDiscoveryListing = (listing, { currentUser, ownListingIds } = {}) => {
  const listingId = listingIdOf(listing);
  const authorId = listingAuthorId(listing);
  const userId = currentUserIdOf(currentUser);
  if (listingId && Array.isArray(ownListingIds) && ownListingIds.includes(listingId)) {
    return true;
  }
  return !!(userId && authorId && userId === authorId);
};

/**
 * Drop the current user's own listings from a discovery list.
 *
 * @param {Array} [listings]
 * @param {Object} [options]
 * @param {Object} [options.currentUser]
 * @param {Array<string>} [options.ownListingIds]
 * @returns {Array}
 */
export const excludeOwnDiscoveryListings = (listings = [], options = {}) => {
  const ownListingIds = [
    ...(options.ownListingIds || []),
    listingIdOf(options.currentUserOwnListing),
  ].filter(Boolean);
  const next = { ...options, ownListingIds };
  if (!next.currentUser && !ownListingIds.length) {
    return listings || [];
  }
  return (listings || []).filter(listing => !isOwnDiscoveryListing(listing, next));
};
